import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Button, Container, Select, Stack, Tabs, Text, Title } from "@mantine/core"
import { useMediaQuery, useReducedMotion } from "@mantine/hooks"
import { Link } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { BrandingFormView } from "../../../../components/BrandingFormView"
import { TabbedFormSkeleton } from "../../../../components/PageSkeletons"
import { resolveWorkspaceTranscriptionMode } from "../../../../components/ReprocessForm/helpers"
import { VoxPageTitle } from "../../../../components/VoxPageTitle"
import classes from "../../settings.module.css"
import { BudgetTab } from "../BudgetTab"
import { ConnectClaudeCard } from "../ConnectClaudeCard"
import { FinishedTranscriptCard } from "../FinishedTranscriptCard"
import {
  resolveSettingsGroup,
  SETTINGS_CARD_ANCHORS,
  SETTINGS_GROUP_META,
  type TSettingsCardAnchor,
  type TSettingsGroup,
} from "../groups"
import { ImageGenerationPanel } from "../panels/ImageGenerationPanel"
import { IntegrationsPanel } from "../panels/IntegrationsPanel"
import { LlmProvidersPanel } from "../panels/LlmProvidersPanel"
import { ModelsPanel } from "../panels/ModelsPanel"
import { PromptPanel } from "../panels/PromptPanel"
import { TranscriptionPanel } from "../panels/TranscriptionPanel"
import { SettingsSearch } from "../SettingsSearch"
import { SettingsSection } from "../SettingsSection"
import { SpeakerRecognitionCard } from "../SpeakerRecognitionCard"
import { SETTINGS_INDEX } from "../settings-index"
import { TranscriptionProvidersCard } from "../TranscriptionProvidersCard"
import type { IBrandingDraft, IViewProps } from "./interfaces"
import local from "./styles.module.css"

function noop(_value: string): void {}

/**
 * Settings — pure presentation. The route wrapper owns the queries, the form
 * state, and the saves. This view is the rail (General, Look, Reports,
 * Integrations, Advanced) and the cards inside each section.
 */
export const SettingsView = ({
  product,
  mcpUrl = null,
  settings,
  configuredSecrets,
  updateSetting,
  save,
  saveKey,
  saving,
  savingFallback,
  isAdminOnly,
  canManage,
  company,
  isDirty,
  isLoading,
  error,
  activeTab,
  onTabChange,
  batchOrder,
  streamingOrder,
  providerHealth,
  resolvedKeys,
  onMoveBatch,
  onSaveBatch,
  onMoveStreaming,
  onSaveStreaming,
  onApplyOrderToAllTenants,
  applyingOrderToAllTenants,
  showAppearance,
  showSummaryLayout,
  summaryLayoutSlot,
  showReportTypes,
  reportTypesSlot,
  companyName,
  branding,
  onBrandingChange,
  onSaveBranding,
  savingBranding,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const isMobile = useMediaQuery("(max-width: 48em)", false)
  const activeGroup = resolveSettingsGroup(activeTab)
  const showWorkspace = !isAdminOnly && !!company
  const showSpeakerRecognition = showWorkspace
  const showFinishedTranscript = isAdminOnly || resolveWorkspaceTranscriptionMode(settings) === "ensemble"

  const railData = useMemo(
    () => SETTINGS_GROUP_META.map(({ value, label }) => ({ value, label: i18n._(label) })),
    [i18n],
  )

  const visibleEntries = useMemo(() => {
    const anchors = SETTINGS_CARD_ANCHORS
    const gated: Record<string, boolean> = {
      [anchors.members]: showWorkspace,
      [anchors.reportTypes]: showReportTypes,
      [anchors.summaryLayout]: showSummaryLayout,
      [anchors.speakerRecognition]: showSpeakerRecognition,
      [anchors.finishedTranscript]: showFinishedTranscript,
    }
    return SETTINGS_INDEX.filter((entry) => gated[entry.anchor] ?? true)
  }, [showWorkspace, showReportTypes, showSummaryLayout, showSpeakerRecognition, showFinishedTranscript])

  const prefersReducedMotion = useReducedMotion()
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null)
  const [highlightAnchor, setHighlightAnchor] = useState<string | null>(null)

  const handleJump = useCallback(
    (group: TSettingsGroup, anchor: TSettingsCardAnchor): void => {
      onTabChange(group)
      setPendingAnchor(anchor)
    },
    [onTabChange],
  )

  useEffect(() => {
    if (!pendingAnchor) return
    const el = typeof document === "undefined" ? null : document.getElementById(pendingAnchor)
    if (!el) return
    if (typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" })
    }
    setHighlightAnchor(pendingAnchor)
    setPendingAnchor(null)
  }, [pendingAnchor, prefersReducedMotion])

  useEffect(() => {
    if (!highlightAnchor) return
    const timer = window.setTimeout(() => setHighlightAnchor(null), 1800)
    return () => window.clearTimeout(timer)
  }, [highlightAnchor])

  const patchBranding = useCallback(
    (patch: Partial<IBrandingDraft>): void => {
      onBrandingChange({ ...branding, ...patch })
    },
    [branding, onBrandingChange],
  )

  if (isLoading) {
    return (
      <Container size="lg" pt="md" pb="xl">
        <TabbedFormSkeleton />
      </Container>
    )
  }

  const pageTitle = isAdminOnly ? (
    <Trans>Platform settings</Trans>
  ) : companyName ? (
    <Trans>{companyName} settings</Trans>
  ) : (
    <Trans>Settings</Trans>
  )
  const dirtyBadge = isDirty ? (
    <Badge ml="sm" color="warning" variant="light" size="sm">
      <Trans>Unsaved changes</Trans>
    </Badge>
  ) : null

  return (
    <Container size="lg" pt="md" pb="xl">
      {product === "voxcanon" ? (
        <VoxPageTitle
          eyebrow={{ primary: i18n._(msg`Workspace`), secondary: i18n._(msg`Configuration`) }}
          leading={isAdminOnly ? <Trans>Platform</Trans> : companyName ? companyName : <Trans>Settings</Trans>}
          accent={isAdminOnly || companyName ? <Trans>settings</Trans> : ""}
          subline={<Trans>Languages, look, reports, integrations, and the technical options.</Trans>}
          actions={dirtyBadge ?? undefined}
        />
      ) : (
        <Title order={2} mb="lg">
          {pageTitle}
          {dirtyBadge}
        </Title>
      )}
      {error && (
        <Alert color="danger" mb="md" variant="light">
          {error}
        </Alert>
      )}

      <div className={classes.searchWrap}>
        <SettingsSearch entries={visibleEntries} onJump={handleJump} />
      </div>

      <Tabs
        value={activeGroup}
        onChange={(value) => value && onTabChange(value)}
        orientation={isMobile ? "horizontal" : "vertical"}
        variant="pills"
        classNames={{
          root: classes.settingsRoot,
          list: classes.settingsNav,
          tab: classes.settingsTab,
          tabLabel: classes.settingsTabLabel,
          panel: classes.settingsPanel,
        }}
      >
        {isMobile ? (
          <Select
            data={railData}
            value={activeGroup}
            onChange={(value) => value && onTabChange(value)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            mb="md"
            aria-label={i18n._(msg`Settings section`)}
          />
        ) : (
          <Tabs.List>
            {SETTINGS_GROUP_META.map(({ value, label, Icon }) => (
              <Tabs.Tab key={value} value={value}>
                <span className={local.tabContent}>
                  <Icon className={local.tabIcon} size={16} aria-hidden />
                  <span className={local.tabWords}>{i18n._(label)}</span>
                </span>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        )}

        <Tabs.Panel value="general">
          <Stack gap="xl">
            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.language}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.language}
              title={<Trans>Languages and cleanup</Trans>}
              description={<Trans>Meeting languages, filler-word cleanup, and company words.</Trans>}
            >
              <TranscriptionPanel
                settings={settings}
                saving={saving}
                isAdminOnly={isAdminOnly}
                canManage={canManage}
                company={company}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>

            {showWorkspace && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.members}
                highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.members}
                title={<Trans>Members</Trans>}
                description={<Trans>Who can sign in, and who can change settings.</Trans>}
              >
                <Button variant="light" renderRoot={(props) => <Link to="/members" {...props} />}>
                  <Trans>Manage members</Trans>
                </Button>
              </SettingsSection>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="look">
          <Stack gap="xl">
            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.appearance}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.appearance}
              title={<Trans>Logo and colour</Trans>}
              description={
                showWorkspace ? (
                  <Trans>Your logo, brand colour, and the default theme.</Trans>
                ) : (
                  <Trans>Each workspace sets its own logo and brand colour.</Trans>
                )
              }
            >
              {showWorkspace ? (
                <BrandingFormView
                  logoUrl={branding.logoUrl}
                  brandColor={branding.brandColor}
                  loginBackgroundUrl={branding.loginBackgroundUrl}
                  defaultTheme={branding.defaultTheme}
                  readOnly={!canManage}
                  saving={savingBranding}
                  showTheme={showAppearance}
                  showIdentity={false}
                  name=""
                  slug=""
                  onLogoUrlChange={(logoUrl) => patchBranding({ logoUrl })}
                  onBrandColorChange={(brandColor) => patchBranding({ brandColor })}
                  onLoginBackgroundUrlChange={(loginBackgroundUrl) => patchBranding({ loginBackgroundUrl })}
                  onDefaultThemeChange={(defaultTheme) => patchBranding({ defaultTheme })}
                  onNameChange={noop}
                  onSlugChange={noop}
                  onSave={onSaveBranding}
                />
              ) : (
                <Text size="sm" c="dimmed">
                  <Trans>Open a workspace to change them.</Trans>
                </Text>
              )}
            </SettingsSection>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="reports">
          <Stack gap="xl">
            {showReportTypes && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.reportTypes}
                highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.reportTypes}
                title={<Trans>Report types</Trans>}
                description={
                  <Trans>Which report styles this workspace can generate, and when the heavy model is allowed.</Trans>
                }
              >
                {reportTypesSlot}
              </SettingsSection>
            )}

            {showSummaryLayout && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.summaryLayout}
                highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.summaryLayout}
                title={<Trans>Meeting layout</Trans>}
                description={<Trans>Arrange the blocks that make up a meeting summary.</Trans>}
              >
                {summaryLayoutSlot}
              </SettingsSection>
            )}

            {!showReportTypes && !showSummaryLayout && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.reportTypes}
                title={<Trans>Reports</Trans>}
                description={<Trans>Report styles and the meeting summary layout are set on each workspace.</Trans>}
              >
                <Text size="sm" c="dimmed">
                  <Trans>Open a workspace to change them.</Trans>
                </Text>
              </SettingsSection>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="integrations">
          <Stack gap="xl">
            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.integrations}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.integrations}
              title={<Trans>Microsoft Teams</Trans>}
              description={<Trans>Connect Microsoft Teams so meetings can show up here.</Trans>}
            >
              <IntegrationsPanel
                settings={settings}
                configuredSecrets={configuredSecrets}
                saving={saving}
                isAdminOnly={isAdminOnly}
                canManage={canManage}
                company={company}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.connectClaude}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.connectClaude}
              title={<Trans>Connect Claude</Trans>}
              description={<Trans>Let Claude read your meetings and ask questions about them.</Trans>}
            >
              <ConnectClaudeCard product={product} mcpUrl={mcpUrl} />
            </SettingsSection>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="advanced">
          <Stack gap="xl">
            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.transcription}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.transcription}
              title={<Trans>Transcription services</Trans>}
              description={<Trans>These services turn speech into text, and the first one is tried first.</Trans>}
            >
              <TranscriptionProvidersCard
                batchOrder={batchOrder}
                streamingOrder={streamingOrder}
                savingFallback={savingFallback}
                health={providerHealth}
                resolvedKeys={resolvedKeys}
                onMoveBatch={onMoveBatch}
                onSaveBatch={onSaveBatch}
                onMoveStreaming={onMoveStreaming}
                onSaveStreaming={onSaveStreaming}
                onApplyOrderToAllTenants={onApplyOrderToAllTenants}
                applyingOrderToAllTenants={applyingOrderToAllTenants}
                settings={settings}
                configuredSecrets={configuredSecrets}
                saving={saving}
                isAdminOnly={isAdminOnly}
                company={company}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>

            {showFinishedTranscript && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.finishedTranscript}
                highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.finishedTranscript}
                title={<Trans>Finished transcript</Trans>}
                description={<Trans>How a finished recording becomes the transcript you keep.</Trans>}
              >
                <FinishedTranscriptCard
                  settings={settings}
                  saving={saving}
                  canManage={canManage}
                  isAdminOnly={isAdminOnly}
                  updateSetting={updateSetting}
                  save={save}
                />
              </SettingsSection>
            )}

            {showSpeakerRecognition && (
              <SettingsSection
                id={SETTINGS_CARD_ANCHORS.speakerRecognition}
                highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.speakerRecognition}
                title={<Trans>Speaker turns and voice matching</Trans>}
                description={<Trans>How voices are told apart and matched to people you already know.</Trans>}
              >
                <SpeakerRecognitionCard
                  settings={settings}
                  saving={saving}
                  canManage={canManage}
                  updateSetting={updateSetting}
                  save={save}
                />
              </SettingsSection>
            )}

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.llmProviders}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.llmProviders}
              title={<Trans>AI models</Trans>}
              description={<Trans>The accounts that write summaries and answers.</Trans>}
            >
              <LlmProvidersPanel
                settings={settings}
                configuredSecrets={configuredSecrets}
                saving={saving}
                isAdminOnly={isAdminOnly}
                updateSetting={updateSetting}
                saveKey={saveKey}
              />
            </SettingsSection>

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.models}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.models}
              title={<Trans>Model selection</Trans>}
              description={<Trans>Which model does each kind of work. Cheaper models use fewer credits.</Trans>}
            >
              <ModelsPanel
                settings={settings}
                saving={saving}
                isAdminOnly={isAdminOnly}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.prompt}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.prompt}
              title={<Trans>Analysis prompt</Trans>}
              description={<Trans>The instructions a meeting summary is written from.</Trans>}
            >
              <PromptPanel
                settings={settings}
                saving={saving}
                canManage={canManage}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.image}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.image}
              title={<Trans>Image generation</Trans>}
              description={<Trans>Where the pictures in reports come from.</Trans>}
            >
              <ImageGenerationPanel
                settings={settings}
                configuredSecrets={configuredSecrets}
                saving={saving}
                isAdminOnly={isAdminOnly}
                canManage={canManage}
                company={company}
                updateSetting={updateSetting}
                saveKey={saveKey}
              />
            </SettingsSection>

            <SettingsSection
              id={SETTINGS_CARD_ANCHORS.budget}
              highlighted={highlightAnchor === SETTINGS_CARD_ANCHORS.budget}
              title={<Trans>Monthly limits</Trans>}
              description={<Trans>A monthly credit limit for each service.</Trans>}
            >
              <BudgetTab
                company={company}
                settings={settings}
                savingSettings={saving}
                canManage={canManage}
                updateSetting={updateSetting}
                save={save}
              />
            </SettingsSection>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Container>
  )
}
